import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, ArrowRight, CheckCircle, PlayCircle,
  ExternalLink, Clock, BookOpen, Volume2, VolumeX, Pause, Square, Loader2, Sparkles
} from 'lucide-react';
import { useTextToSpeech, stripHtml } from '../../hooks/useTextToSpeech';
import { useElevenLabsTTS } from '../../hooks/useElevenLabsTTS';
import { useTimeTracking } from '../../hooks/useTimeTracking';

const API = API_BASE;

export default function TrainingLesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [useElevenLabs, setUseElevenLabs] = useState(true);
  const [hasNextLesson, setHasNextLesson] = useState(true);  // NEW: Track if there's a next lesson
  const [moduleQuizId, setModuleQuizId] = useState(null);  // Quiz ID for current module (if any)
  const videoRef = useRef(null);
  
  // Text-to-Speech hooks - both options available
  const webSpeech = useTextToSpeech();
  const elevenLabs = useElevenLabsTTS();
  
  // Select active TTS based on user preference
  const tts = useElevenLabs ? elevenLabs : webSpeech;
  const { speak, stop, speaking, paused, toggle, supported: ttsSupported, loading: ttsLoading } = tts;

  // Centralised time tracking — pauses while tab is hidden, no double counting.
  useTimeTracking(lessonId, !!lessonId);

  useEffect(() => {
    fetchLesson();
  }, [lessonId]);

  const fetchLesson = async () => {
    try {
      const response = await axios.get(`${API}/training/lessons/${lessonId}`);
      setLesson(response.data);
      
      // Restore previous progress
      const progress = response.data.user_progress || {};
      setVideoProgress(progress.video_progress || 0);
      setCompleted(progress.completed || false);
      
      // Check if there's a next lesson
      if (response.data.module_id) {
        try {
          const moduleRes = await axios.get(`${API}/training/modules/${response.data.module_id}`);
          const lessons = moduleRes.data.lessons || [];
          const currentIndex = lessons.findIndex(l => l.id === lessonId);
          setHasNextLesson(currentIndex !== -1 && currentIndex < lessons.length - 1);
          // Capture module quiz id so the trainee can jump directly to the quiz
          // when they finish the last lesson.
          setModuleQuizId(moduleRes.data.quiz?.id || null);
        } catch (e) {
          setHasNextLesson(false);
          setModuleQuizId(null);
        }
      }
    } catch (error) {
      console.error('Error fetching lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (forceComplete = false) => {
    if (!lesson) return;
    
    try {
      // NOTE: time_spent_minutes is INTENTIONALLY NOT sent here.
      // Time tracking is exclusively handled by useTimeTracking(lessonId) above to
      // avoid double counting. saveProgress only persists video position + completion.
      await axios.post(`${API}/training/lessons/${lessonId}/progress`, {
        video_progress: videoProgress,
        completed: forceComplete || completed || videoProgress >= 95
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleVideoProgress = (e) => {
    if (e.target.duration) {
      const progress = (e.target.currentTime / e.target.duration) * 100;
      setVideoProgress(progress);
      
      // Auto-complete if watched 95%+
      if (progress >= 95 && !completed) {
        setCompleted(true);
        saveProgress(true);
      }
    }
  };

  const markAsCompleted = async () => {
    setCompleted(true);
    await saveProgress(true);
  };

  const goToNextLesson = async () => {
    // Try to get next lesson from module
    try {
      const moduleRes = await axios.get(`${API}/training/modules/${lesson.module_id}`);
      const lessons = moduleRes.data.lessons || [];
      const currentIndex = lessons.findIndex(l => l.id === lessonId);
      
      if (currentIndex !== -1 && currentIndex < lessons.length - 1) {
        // Go to next lesson
        const nextLesson = lessons[currentIndex + 1];
        navigate(`/training/lesson/${nextLesson.id}`);
      } else if (moduleRes.data.quiz?.id) {
        // Last lesson + module has a quiz → go take the quiz
        navigate(`/training/quiz/${moduleRes.data.quiz.id}`);
      } else {
        // No more lessons, no quiz → back to module
        navigate(`/training/module/${lesson.module_id}`);
      }
    } catch (error) {
      console.error('Error getting next lesson:', error);
      navigate(`/training/module/${lesson.module_id}`);
    }
  };

  const lang = user?.training_language || 'en';
  const t = (en, es) => lang === 'es' ? es : en;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('Lesson not found', 'Lección no encontrada')}</p>
        <Button onClick={() => navigate('/training')} className="mt-4">
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
      </div>
    );
  }

  const content = lang === 'es' ? lesson.content_es : lesson.content_en;
  const videoUrl = lang === 'es' ? lesson.video_url_es : lesson.video_url_en;

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(`/training/module/${lesson.module_id}`)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('Back to Module', 'Volver al Módulo')}
      </Button>

      {/* Lesson Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">
                  {t('Lesson', 'Lección')} {lesson.lesson_number}
                </Badge>
                {completed && (
                  <Badge variant="success">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('Completed', 'Completado')}
                  </Badge>
                )}
              </div>
              <CardTitle className="text-2xl">
                {lang === 'es' ? lesson.title_es : lesson.title_en}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{lesson.duration_minutes} min</span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Video Content */}
      {lesson.type === 'video' && videoUrl && (
        <Card>
          <CardContent className="p-0">
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                className="w-full h-full"
                onTimeUpdate={handleVideoProgress}
                onEnded={() => markAsCompleted()}
              >
                {t('Your browser does not support video', 'Tu navegador no soporta video')}
              </video>
            </div>
            <div className="p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>{t('Watch Progress', 'Progreso de Video')}</span>
                <span>{videoProgress.toFixed(0)}%</span>
              </div>
              <Progress value={videoProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Text Content - Only show if there's actual content and not a quiz type */}
      {lesson.type !== 'quiz' && content && content.trim() && (
        <Card>
          {/* TTS Controls */}
          {content && (
            <div className="flex flex-wrap items-center gap-3 p-4 border-b bg-muted/30">
              {/* Play/Pause Button */}
              <Button
                variant={speaking ? "default" : "outline"}
                size="sm"
                disabled={ttsLoading}
                onClick={() => {
                  if (speaking) {
                    toggle();
                  } else {
                    const textContent = stripHtml(content);
                    // Pass lessonId for server-side caching - saves ElevenLabs credits!
                    speak(textContent, { lang, rate: 0.9, lessonId: lesson.id });
                  }
                }}
              >
                {ttsLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('Generating...', 'Generando...')}
                  </>
                ) : speaking && !paused ? (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    {t('Pause', 'Pausar')}
                  </>
                ) : speaking && paused ? (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {t('Resume', 'Continuar')}
                  </>
                ) : (
                  <>
                    <Volume2 className="h-4 w-4 mr-2" />
                    {t('Listen', 'Escuchar')}
                  </>
                )}
              </Button>
              
              {speaking && (
                <Button variant="ghost" size="sm" onClick={stop}>
                  <Square className="h-4 w-4 mr-2" />
                  {t('Stop', 'Detener')}
                </Button>
              )}
              
              {/* TTS Engine Toggle */}
              <div className="flex items-center gap-2 ml-auto border-l pl-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="tts-mode"
                    checked={useElevenLabs}
                    onCheckedChange={(checked) => {
                      // Stop current playback when switching
                      stop();
                      webSpeech.stop();
                      elevenLabs.stop();
                      setUseElevenLabs(checked);
                    }}
                    disabled={speaking || ttsLoading}
                  />
                  <Label htmlFor="tts-mode" className="text-xs cursor-pointer flex items-center gap-1">
                    {useElevenLabs ? (
                      <>
                        <Sparkles className="h-3 w-3 text-amber-500" />
                        <span className="hidden sm:inline">ElevenLabs AI</span>
                        <span className="sm:hidden">AI</span>
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-3 w-3 text-muted-foreground" />
                        <span className="hidden sm:inline">{t('Browser', 'Navegador')}</span>
                      </>
                    )}
                  </Label>
                </div>
              </div>
              
              {/* Status indicator */}
              <span className="text-xs text-muted-foreground w-full mt-1 sm:mt-0 sm:w-auto">
                {useElevenLabs 
                  ? t('🎙️ High-quality AI voice', '🎙️ Voz AI de alta calidad')
                  : t('🔊 Browser narration', '🔊 Narración del navegador')
                }
              </span>
            </div>
          )}
          <CardContent className="p-6 prose prose-slate max-w-none">
            <div dangerouslySetInnerHTML={{ __html: content || '' }} />
          </CardContent>
        </Card>
      )}

      {/* External Link */}
      {lesson.type === 'link' && lesson.external_url && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" />
                  {t('External Resource', 'Recurso Externo')}
                </h3>
                <p className="text-muted-foreground mt-1">
                  {lang === 'es' ? lesson.link_description_es : lesson.link_description_en}
                </p>
              </div>
              <Button asChild>
                <a href={lesson.external_url} target="_blank" rel="noopener noreferrer">
                  {t('Open Link', 'Abrir Enlace')}
                  <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quiz Section - REMOVED (evaluations are now in-person) */}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={() => navigate(`/training/module/${lesson.module_id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Module', 'Volver al Módulo')}
        </Button>

        <div className="flex items-center gap-4">
          {/* Show complete button if not completed and: not a video, OR video without URL */}
          {!completed && (lesson.type !== 'video' || !videoUrl) && (
            <Button onClick={markAsCompleted} variant="default">
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('Mark as Completed', 'Marcar como Completado')}
            </Button>
          )}
          
          {/* Always show Next Lesson button (or Take Quiz if last lesson + module has quiz) */}
          <Button 
            onClick={goToNextLesson}
            variant={completed ? "default" : "outline"}
            data-testid="lesson-next-btn"
          >
            {hasNextLesson
              ? t('Next Lesson', 'Próxima Lección')
              : moduleQuizId
                ? t('Take Module Quiz', 'Tomar Quiz del Módulo')
                : t('Return to Module', 'Volver al Módulo')
            }
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
